/**
 * GroupStorage - 群組隔離的 LocalStorage 封裝
 *
 * 為所有 localStorage 操作自動加上 groupId 前綴，確保不同群組的資料不會互相干擾。
 * 專為 Lite 功能設計，使用 groupId（C 開頭的 33 字元字串）而非 tenantId。
 *
 * 使用方式：
 * ```typescript
 * const groupId = getGroupIdFromUrl();
 * const storage = new GroupStorage(groupId);
 * storage.setItem('activity', activityData);
 * const activity = storage.getItem('activity');
 * ```
 */
export class GroupStorage {
  private groupId: string;
  private separator = '::'; // 分隔符號，避免與內容混淆

  constructor(groupId: string) {
    if (!groupId || groupId.trim() === '') {
      throw new Error('Group ID is required for storage initialization');
    }
    this.groupId = groupId.trim();
  }

  /**
   * 產生帶有 namespace 的 key
   * 例如: "Ceb...::activity_list"
   */
  private _getKey(key: string): string {
    return `${this.groupId}${this.separator}${key}`;
  }

  /**
   * 儲存資料到 localStorage
   * @param key - 資料鍵值
   * @param value - 要儲存的資料（會自動序列化為 JSON）
   */
  setItem<T>(key: string, value: T): void {
    try {
      const namespacedKey = this._getKey(key);
      const stringValue = JSON.stringify(value);
      localStorage.setItem(namespacedKey, stringValue);
    } catch (error) {
      console.error(`[GroupStorage] 儲存失敗: ${key}`, error);
      throw new Error(
        `無法儲存資料到 localStorage: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * 從 localStorage 讀取資料
   * @param key - 資料鍵值
   * @returns 讀取的資料（會自動反序列化 JSON），如果不存在則返回 null
   */
  getItem<T>(key: string): T | null {
    try {
      const namespacedKey = this._getKey(key);
      const value = localStorage.getItem(namespacedKey);

      if (value === null) {
        return null;
      }

      // 嘗試解析 JSON
      try {
        return JSON.parse(value) as T;
      } catch (e) {
        // 如果解析失敗，可能是舊格式的純字串，直接返回
        console.warn(`[GroupStorage] JSON 解析失敗，返回原始字串: ${key}`, e);
        return value as unknown as T;
      }
    } catch (error) {
      console.error(`[GroupStorage] 讀取失敗: ${key}`, error);
      return null;
    }
  }

  /**
   * 從 localStorage 刪除資料
   * @param key - 資料鍵值
   */
  removeItem(key: string): void {
    try {
      const namespacedKey = this._getKey(key);
      localStorage.removeItem(namespacedKey);
    } catch (error) {
      console.error(`[GroupStorage] 刪除失敗: ${key}`, error);
    }
  }

  /**
   * 清除此群組的所有資料
   * 會掃描所有 localStorage 的 key，找出屬於此群組的資料並刪除
   */
  clear(): void {
    try {
      const prefix = `${this.groupId}${this.separator}`;
      const keysToRemove: string[] = [];

      // 掃描所有 localStorage 的 key
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      // 刪除所有找到的 key
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      console.log(
        `[GroupStorage] 已清除群組 ${this.groupId} 的 ${keysToRemove.length} 筆資料`
      );
    } catch (error) {
      console.error(`[GroupStorage] 清除失敗:`, error);
    }
  }

  /**
   * 取得此群組的 groupId
   */
  getGroupId(): string {
    return this.groupId;
  }
}
